/**
 * Parse PageTest AI suggestion markdown into structured blocks.
 * Falls back gracefully when format does not match.
 */
export function parseAiSuggestions(markdown) {
  if (!markdown || typeof markdown !== "string") {
    return { summary: "", issues: [], raw: markdown || "" };
  }

  const summaryMatch = markdown.match(
    /###\s*PageTest Summary\s*\n([\s\S]*?)(?=\n###\s*Issues|\n####\s|\Z)/i
  );
  const summary = summaryMatch ? cleanInline(summaryMatch[1]) : "";

  const issuesPart =
    markdown.match(/###\s*Issues & Fixes\s*\n([\s\S]*)/i)?.[1] || markdown;

  const blocks = issuesPart.split(/(?:^|\n)#{4}\s+/m).filter(Boolean);
  const issues = [];

  for (const block of blocks) {
    const parsed = parseIssueBlock(block);
    if (parsed) issues.push(parsed);
  }

  return { summary, issues, raw: markdown };
}

function parseIssueBlock(block) {
  const lines = block.split("\n");
  if (!lines.length) return null;

  const rawTitleLine = lines[0] || "";
  const numberMatch = rawTitleLine.match(/^(\d+)\.\s*/);
  const issueNumber = numberMatch ? Number(numberMatch[1]) : null;
  const title = normalizeTitle(rawTitleLine);

  let category = "";
  let finding = "";
  let affected = [];
  let fix = [];
  let mode = null;

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const categoryMatch = line.match(/^[-*]?\s*\*\*Category:\*\*\s*(.+)$/i);
    if (categoryMatch) {
      category = cleanInline(categoryMatch[1]);
      mode = null;
      continue;
    }

    const findingMatch = line.match(/^[-*]?\s*\*\*Finding:\*\*\s*(.+)$/i);
    if (findingMatch) {
      finding = cleanInline(findingMatch[1]);
      mode = null;
      continue;
    }

    const affectedMatch = line.match(/^[-*]?\s*\*\*Affected:\*\*\s*(.*)$/i);
    if (affectedMatch) {
      mode = "affected";
      const inline = cleanInline(affectedMatch[1]);
      if (inline) affected.push(inline);
      continue;
    }

    const fixMatch = line.match(/^[-*]?\s*\*\*Fix:\*\*\s*(.*)$/i);
    if (fixMatch) {
      mode = "fix";
      const inline = cleanInline(fixMatch[1]);
      if (inline) fix.push(inline);
      continue;
    }

    const numbered = line.match(/^\d+\.\s+(.+)$/);
    if (numbered && mode === "fix") {
      fix.push(cleanInline(numbered[1]));
      continue;
    }

    const bullet = line.match(/^[-*]\s+(.+)$/);
    if (bullet) {
      const text = cleanInline(bullet[1]);
      if (mode === "affected") affected.push(text);
      else if (mode === "fix") fix.push(text);
      else if (!category && !finding) affected.push(text);
      continue;
    }

    if (mode === "affected") affected.push(cleanInline(line));
    else if (mode === "fix") fix.push(cleanInline(line));
  }

  if (!title) return null;

  return {
    title,
    issueNumber,
    category: category || "performance",
    finding,
    affected: affected.filter(Boolean),
    fix: fix.filter(Boolean),
  };
}

function normalizeTitle(line) {
  if (!line) return "";
  return line
    .replace(/^#+\s*/, "")
    .replace(/^\d+\.\s*/, "")
    .trim();
}

function cleanInline(text) {
  if (!text) return "";
  return text
    .replace(/\*\*/g, "")
    .replace(/`/g, "")
    .replace(/^\s*[-*•]\s*/, "")
    .trim();
}

export function categoryVariant(cat) {
  const c = (cat || "").toLowerCase();
  if (c.includes("access")) return "success";
  if (c.includes("seo")) return "outline";
  if (c.includes("best")) return "warning";
  if (c.includes("perf")) return "default";
  return "outline";
}
